/*
 * Telling "the server has not built this yet" apart from "this failed".
 *
 * ---------------------------------------------------------------------------
 * WHY (reported 2026-10-28)
 * ---------------------------------------------------------------------------
 * An operator pressed "Create this Project now" and was told *"The mapping
 * could not be applied. Nothing has been created."* The button was correct,
 * the selector was correct, and nothing was wrong with the request. The route
 * simply does not exist: the server's `ProjectMappingRoutes` registers §3.1
 * (dashboard), §3.2 (plan) and §3.3 (review) and says in terms that §3.4-§3.8
 * -- decide, apply, run, revert, handover -- "are not registered at all",
 * because answering them with a placeholder would be worse than a 404.
 *
 * That reasoning is right, and this module is the client half of it. A 404
 * from an unbuilt route is a FACT ABOUT THE DEPLOYMENT, not a failed attempt,
 * and the two deserve different sentences:
 *
 *   - "could not be applied"   -> something went wrong; try again, call support.
 *   - "not available yet"      -> nothing is wrong; trying again cannot help.
 *
 * Shown as the former, every write action on the review screen reads as an
 * intermittent outage. An operator retries, retries from another browser, and
 * eventually reports a bug against a screen that is behaving exactly as
 * designed against a server that has not shipped its half yet.
 *
 * ---------------------------------------------------------------------------
 * HOW THE TWO ARE TOLD APART
 * ---------------------------------------------------------------------------
 * There is no custom 404 handler in the server's Express app -- `errorMiddleware`
 * is a four-argument error handler and never sees an unmatched route -- so an
 * unregistered path falls through to Express's default, which answers 404 with
 * an HTML page and no JSON body. `parseServerError` therefore reports
 * `httpStatus = 404` and an EMPTY `code`.
 *
 * Every 404 these endpoints will emit once they are built carries a SmartError,
 * and so a non-empty `code` (`PROJECT_MAPPING_RUN_NOT_FOUND` and friends) --
 * exactly as §3.3 already does for `PROJECT_MAPPING_NOT_PLANNED`, which
 * `mapping-not-ready.ts` branches on today. So "404 with no machine code" is
 * the signature of a route that is not there, and it stops being true the
 * moment the server registers one. That is the intended lifetime of this
 * module: when §3.4-§3.8 ship, this stops matching and the ordinary failure
 * sentences take over again with no further change here.
 *
 * Deliberately NOT keyed on the HTML body or on any sentence in it. Prose is
 * not a contract, and a proxy, a WAF or a future catch-all could replace it
 * without warning.
 *
 * ---------------------------------------------------------------------------
 * THE INVERSE MISTAKE, AND WHY `details` IS ALSO CHECKED (server, 2026-10-28c)
 * ---------------------------------------------------------------------------
 * The server verified both assumptions above and then pointed out the hole:
 * their `errorMiddleware` builds its body as `{ error: message }` **with no
 * `statusCode`**. So anything that THROWS its way out as a 404 produces a 404
 * with no machine code -- which the status-and-code test alone would read as
 * "not built yet" and use to disable the whole screen. That is the inverse of
 * the bug this module was written for, and a worse one: a real fault would be
 * reported as a feature that has not shipped.
 *
 * It is not reachable today (nothing in their `src/` throws a SmartError; they
 * are returned), but it is one `throw` away, so it is guarded rather than
 * trusted. An unmatched route produces an HTML body, which `extractBody` keys
 * as `markup` precisely so no display path can reach it -- leaving `details`
 * EMPTY. A thrown error leaves `details` carrying the server's sentence. So
 * "404, no machine code, and nothing a human could be shown" is the signature,
 * and a throw-produced 404 now falls through to the ordinary failure path with
 * its sentence intact.
 *
 * Kept free of @angular imports so tests/ProjectMappingWriteUnavailable.smoke.ts
 * can assert it under plain ts-node, in the same way as `mapping-not-ready.ts`.
 */
import { parseServerError } from '../../service-data/http-error';

/**
 * True when a failed call looks like an endpoint the server has not built.
 *
 * Conservative on purpose. A transport failure (`httpStatus` 0), a 500, any
 * 404 that carried a machine code, and any 404 that carried a sentence for a
 * human all answer `false` and are reported as the genuine failures they are
 * -- claiming "not available yet" about a real outage would be the same
 * mistake in the other direction, and it would disable the screen.
 */
export function isEndpointNotBuilt(error: any): boolean {
  const info = parseServerError(error);
  return info.httpStatus === 404 && !info.code && !info.details;
}
